import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@aibuild/ui';
import { appsInTab, type AppTab } from '../../pages/Apps/appTabs';
import FooterAppTile from './FooterAppTile';
import FooterCreateTile from './FooterCreateTile';
import FooterRow from './FooterRow';
import type { FooterApps } from './useFooterApps';
import { Note } from './AppsFooter.styled';

const APPS_PATH = '/apps';

interface FooterAppsContentProps {
  readonly data: FooterApps;
  readonly tab: AppTab;
  readonly grid: boolean;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
}

const FooterAppsContent: React.FC<FooterAppsContentProps> = ({ data, tab, grid, canCreate, onCreate }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  if (data.isLoading) {
    return (
      <Note>
        <Spinner size="sm" />
      </Note>
    );
  }

  const failures = [
    data.catalogFailed &&
      intl.formatMessage({ id: 'apps.gallery.error.title', defaultMessage: 'Apps could not be loaded' }),
    data.mineFailed &&
      intl.formatMessage({
        id: 'apps.gallery.section.mine.error',
        defaultMessage: 'Your apps could not be loaded. Reload the page to try again.',
      }),
  ].filter((message): message is string => Boolean(message));
  const visible = appsInTab(data.apps, tab);
  const offersCreate = canCreate && (tab === 'all' || tab === 'personal');
  const empty = visible.length === 0 && !offersCreate;

  return (
    <>
      {failures.map((message) => (
        <Note key={message} role="alert">
          {message}
        </Note>
      ))}
      {empty && failures.length === 0 && (
        <Note data-testid="apps-footer__empty">
          {intl.formatMessage({ id: 'appsFooter.empty', defaultMessage: 'No apps here yet.' })}
        </Note>
      )}
      {!empty && (
        <FooterRow
          grid={grid}
          label={intl.formatMessage({ id: 'apps.gallery.title', defaultMessage: 'Apps' })}
        >
          {offersCreate && <FooterCreateTile onCreate={onCreate} />}
          {visible.map((app) => (
            <FooterAppTile
              key={app.appKey}
              app={app}
              grow={!grid}
              onOpen={() => navigate(app.openPath)}
              onAllApps={grid ? undefined : () => navigate(APPS_PATH)}
            />
          ))}
        </FooterRow>
      )}
    </>
  );
};

export default FooterAppsContent;
